import { Botao, CabecalhoPagina, EstadoVazio, Esqueleto } from '@persona/ui';
import { useQueryClient } from '@tanstack/react-query';
import { Link, Outlet, useNavigate, useParams, useRouterState } from '@tanstack/react-router';
import { Ban, Construction, FileQuestion, KeyRound, Stethoscope } from 'lucide-react';
import { lazy, Suspense, useEffect, useRef } from 'react';
import { telasRotina } from './telasRotina';
import { inclusaoRotina } from './inclusaoRotina';
import { ErroApi, mensagemDe } from '../api/erros';
import { useEsquema } from '../dicionario/useEsquema';
import type { ItemIndice } from '../navegacao/busca';
import type { MenuModulo } from '../navegacao/indice';
import { moduloDaRotina } from '../navegacao/moduloDaRotina';
import { useRecentes } from '../navegacao/preferencias';
import { aplicarNivel, mapaRotaNivel } from '../nivel/mapaRotaNivel';
import { useAbrirAba } from '../../shell/BarraAbas';
import { daRotina } from '../../shell/abas';
import { moduloContexto, publicarModulo } from '../../shell/Shell';
import { ContextoEsquema } from './contexto';
import { JanelaRegistro } from './JanelaRegistro';
import { PaginaLista } from './PaginaLista';

const TelaFonte = lazy(() => import('../fontes/TelaFonte'));

/** O que a rota mostra dentro da rotina: a lista, um registro (ver, alterar, incluir, copiar) ou outra tela. */
export function subRotaRotina(caminho: string): 'lista' | 'registro' | 'outra' {
  const resto = caminho.replace(/^\/r\/[^/]+/, '').replace(/\/$/, '');
  if (resto === '') return 'lista';
  return /^\/(novo|\d+(\/(editar|copiar))?)$/.test(resto) ? 'registro' : 'outra';
}

/** /r/:rotina — carrega o esquema (cache + If-None-Match), abre a aba, registra em recentes e refina o nível pelo padrão. */
export function PaginaRotina() {
  const { rotina } = useParams({ from: '/app/r/$rotina' });
  const codigo = rotina.toUpperCase();
  const navegar = useNavigate();
  const local = useRouterState({ select: (s) => s.location });
  const caminho = local.pathname + (local.searchStr ?? '');
  const esquema = useEsquema(codigo);
  const abrirAba = useAbrirAba();
  const { registrar } = useRecentes();
  const cliente = useQueryClient();
  const titulo = esquema.data?.titulo;

  useEffect(() => {
    if (!esquema.data) return;
    const contexto = moduloContexto();
    publicarModulo(moduloDaRotina(codigo, esquema.data.modulo, contexto, cliente.getQueryData<MenuModulo>(['menu', contexto ?? undefined]),
      cliente.getQueryData<ItemIndice[]>(['indice'])));
    aplicarNivel(mapaRotaNivel(local.pathname, esquema.data.padrao));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- o contexto é o módulo de onde o usuário veio, lido na troca de rotina
  }, [esquema.data, local.pathname]);

  useEffect(() => {
    // Saindo da rotina (para o início ou um módulo) a localização muda antes de a página desmontar: sem esta guarda a aba
    // guardava "/" ou "/m/com" como último lugar, e clicar nela depois levava para lá em vez de voltar à rotina.
    if (!titulo || !daRotina(local.pathname, codigo)) return;
    abrirAba(codigo, titulo, caminho);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [codigo, titulo, caminho]);

  useEffect(() => {
    if (titulo) registrar(codigo);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [codigo, titulo]);

  // Fechou a janela do registro: o foco volta para a grade, como quando a lista abria do zero (07 §0 princípio 1).
  const sub = subRotaRotina(local.pathname);
  const subAnterior = useRef(sub);
  useEffect(() => {
    const veioDoRegistro = subAnterior.current === 'registro';
    subAnterior.current = sub;
    if (!veioDoRegistro || sub !== 'lista') return undefined;
    const quadro = requestAnimationFrame(() => document.querySelector<HTMLElement>('[data-testid="grade"]')?.focus());
    return () => cancelAnimationFrame(quadro);
  }, [sub]);

  if (esquema.isLoading) {
    return (
      <div className="pagina" aria-busy="true">
        <div className="esqueleto" style={{ height: 28, width: 280 }} />
        <Esqueleto linhas={8} />
      </div>
    );
  }
  if (esquema.isError) return <ErroRotina codigo={codigo} erro={esquema.error} aoTentar={() => void esquema.refetch()} />;
  const fonte = esquema.data!.fonte;
  if (fonte) {
    return (
      <ContextoEsquema.Provider value={esquema.data!}>
        <div className="pagina">
          <CabecalhoPagina titulo={esquema.data!.titulo} codigo={codigo} />
          <Suspense fallback={<Esqueleto linhas={8} />}>
            <TelaFonte nome={fonte} rotina={codigo} />
          </Suspense>
        </div>
      </ContextoEsquema.Provider>
    );
  }
  const componente = esquema.data!.componente;
  if (componente) {
    const Tela = telasRotina[componente];
    if (!Tela) return <EstadoVazio icone={<Construction size={48} strokeWidth={1.25} />} titulo="Tela indisponível nesta versão" texto={`${codigo} usa a tela "${componente}", que este cliente web não conhece. Atualize a página.`} />;
    return (
      <ContextoEsquema.Provider value={esquema.data!}>
        <Suspense fallback={<div className="pagina" aria-busy="true"><Esqueleto linhas={8} /></div>}>
          <Tela codigo={codigo} />
        </Suspense>
      </ContextoEsquema.Provider>
    );
  }
  // A lista fica montada no mesmo lugar ao abrir um registro: ele vem numa janela por cima, e ao fechar a lista está como estava.
  if (sub === 'outra') {
    return (
      <ContextoEsquema.Provider value={esquema.data!}>
        <Outlet />
      </ContextoEsquema.Provider>
    );
  }
  return (
    <ContextoEsquema.Provider value={esquema.data!}>
      <div className="rotina-lista" inert={sub === 'registro' || undefined}>
        <PaginaLista />
      </div>
      {sub === 'registro' && (inclusaoRotina[codigo as keyof typeof inclusaoRotina] && /\/novo\/?$/.test(local.pathname)
        ? <JanelaRegistro titulo={esquema.data!.titulo}><Suspense fallback={<Esqueleto forma="campo" linhas={6} />}>
          {(() => { const Inclusao = inclusaoRotina[codigo as keyof typeof inclusaoRotina]; return <Inclusao
          aoFechar={() => void navegar({ to: '/r/$rotina', params: { rotina: codigo } })}
          aoAbrir={(id) => void navegar({ to: '/r/$rotina/$id', params: { rotina: codigo, id: String(id) } })} />; })()}</Suspense></JanelaRegistro>
        : <JanelaRegistro titulo={esquema.data!.titulo}><Outlet /></JanelaRegistro>)}
    </ContextoEsquema.Provider>
  );
}

function ErroRotina({ codigo, erro, aoTentar }: { codigo: string; erro: unknown; aoTentar: () => void }) {
  const m = mensagemDe(erro);
  const e = erro instanceof ErroApi ? erro : null;
  const diagnostico = <Link to="/r/$rotina/diagnostico" params={{ rotina: codigo }} className="botao"><Stethoscope size={16} aria-hidden="true" /> Diagnóstico</Link>;
  if (e?.codigo.startsWith('LICENCA_MODULO') || e?.codigo === 'MODULO_NAO_LICENCIADO')
    return <EstadoVazio icone={<KeyRound size={48} strokeWidth={1.25} />} titulo="Módulo não licenciado nesta instalação" texto={`${codigo}: ${m.texto}`} />;
  if (e?.status === 403)
    return <EstadoVazio icone={<Ban size={48} strokeWidth={1.25} />} titulo="Você não tem acesso a esta rotina" texto={`${m.texto} Peça a permissão ao administrador.`} acao={diagnostico} />;
  if (e?.status === 404)
    return <EstadoVazio icone={<FileQuestion size={48} strokeWidth={1.25} />} titulo={`Rotina ${codigo} não encontrada`} texto={m.texto} acao={<Link to="/" className="botao">Voltar ao início</Link>} />;
  return (
    <EstadoVazio icone={<FileQuestion size={48} strokeWidth={1.25} />} titulo="Não foi possível abrir a rotina" texto={`${m.texto}${m.codigo ? ` (${m.codigo})` : ''}`}
      acao={<div style={{ display: 'flex', gap: 8 }}><Botao onClick={aoTentar}>Tentar de novo</Botao>{diagnostico}</div>} />
  );
}
